import { useEffect, useState } from "react";

import { SectionLabel } from "../SectionLabel";
import { BrandLogoCard } from "./BrandLogoCard";
import { TestimonialCard } from "./TestimonialCard";

export type BrandLogo = { name: string; logo: string };

const SIGNUP_URL = "https://mycouponbook.ca/signup/form";

// Shown until real online/local brands load (or if none have logos).
const fallbackBrands: BrandLogo[] = [
  { name: "Chatime", logo: "/images/Webp/Brand1.webp" },
  { name: "Pho Maniac", logo: "/images/Webp/Brand2.webp" },
  { name: "Dyson", logo: "/images/Webp/Brand3.webp" },
  { name: "Pho Maniac", logo: "/images/Webp/Brand4.webp" },
  { name: "Brothers Barbershop", logo: "/images/Webp/Brand5.webp" },
  { name: "Partner brand", logo: "/images/Webp/Brand6.webp" },
  { name: "Gong Cha", logo: "/images/Webp/Brand7.webp" },
  { name: "East Coast Dog", logo: "/images/Webp/Brand8.webp" },
  { name: "Chatime", logo: "/images/Webp/Brand9.webp" },
  { name: "Dyson", logo: "/images/Webp/Brand10.webp" },
];

const testimonials = [
  {
    name: "David Rafuse",
    title: "CEO/Founder, Blended Athletics Inc.",
    image: "/images/Webp/DaviRafusePP.webp",
    quote:
      "What Nelson is building with My Couponbook is exactly what local businesses need. It's a practical tool that drives real value for small businesses and strengthens the communities they serve. I've been impressed by Nelson's vision and the genuine impact this platform can have at the local level.",
  },
  {
    name: "Joel George",
    title: "Owner, Fresco Italiano",
    image: "/images/Webp/Joel George.webp",
    quote:
      "Between high fees and subscriptions from other apps, it's hard for local shops to get ahead. The fact that My Couponbook gives us a free platform to connect directly with our neighborhood shows they actually want to be a true partner to small businesses, rather than just another monthly expense.",
  },
  {
    name: "Dejhani Allen",
    title: "Owner, Brawta Jamaican Jerk Joint",
    image: "/images/Webp/Dejhani Allen.webp",
    quote:
      "Working with My Couponbook has been great! Nelson is supportive and friendly and accommodates our changes when needed. I would encourage any business to use the app!",
  },
  {
    name: "Jon Bowman",
    title: "Owner, Valley West Auto",
    image: "/images/Webp/JonBowman.webp",
    quote:
      "Instead of paying for expensive local ads, My Couponbook lets us connect with our customers and pass marketing savings directly to them. We stay busy, and they get the best value.",
  },
  {
    name: "Robert Johnson",
    title: "Owner, The Corner Bakery",
    image: "/images/Webp/ellipse-3.webp",
    quote:
      "My Couponbook completely changed how we attract local customers. Instead of waiting for walk-ins, we now reach people at the right moment. The push notifications alone bring in new customers every week.",
  },
];

// Two testimonials per slide on desktop, one on smaller screens.
const useItemsPerSlide = () => {
  const query = "(min-width: 1024px)";
  const [items, setItems] = useState(() => (window.matchMedia(query).matches ? 2 : 1));

  useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = () => setItems(mql.matches ? 2 : 1);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  return items;
};

type TrustedBrandsSectionProps = {
  brands?: BrandLogo[];
};

export const TrustedBrandsSection = ({ brands = [] }: TrustedBrandsSectionProps) => {
  const marqueeBrands = brands.length > 0 ? brands : fallbackBrands;

  const itemsPerSlide = useItemsPerSlide();
  const totalSlides = Math.ceil(testimonials.length / itemsPerSlide);
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    setCurrent((prev) => Math.min(prev, totalSlides - 1));
  }, [totalSlides]);

  useEffect(() => {
    const interval = setInterval(() => setCurrent((prev) => (prev + 1) % totalSlides), 6000);
    return () => clearInterval(interval);
  }, [totalSlides]);

  const slides = Array.from({ length: totalSlides }, (_, i) =>
    testimonials.slice(i * itemsPerSlide, i * itemsPerSlide + itemsPerSlide)
  );

  return (
    <section className="w-full px-[16px] sm:px-6 lg:px-[80px] pt-16 lg:pt-[170px] pb-16 lg:pb-20">
      <div className="mx-auto w-full max-w-[1620px] text-center">
        <SectionLabel>Built on Trust</SectionLabel>

        <h2 className="mt-6 lg:mt-12 text-[#171717] font-normal leading-[1.2] tracking-[-1.5px] text-[32px] sm:text-[44px] lg:text-[64px]">
          Trusted by 100s of Local and
          <br className="hidden sm:block" /> National Brands
        </h2>

        <p className="mt-[16px] lg:mt-8 mx-auto max-w-[740px] text-[#2A2A2A] text-[15px] lg:text-[20px] leading-[1.4]">
          Join hundreds of businesses using My Couponbook to build lasting relationships, retain customers, and grow
          their revenue.
        </p>

        {/* Brand marquee — list rendered twice so translating by -50% loops seamlessly */}
        <div className="brand-marquee mt-10 lg:mt-14 -mx-[16px] sm:mx-0 overflow-hidden py-[8px] [-webkit-mask-image:linear-gradient(90deg,transparent_0%,#000_6%,#000_94%,transparent_100%)] [mask-image:linear-gradient(90deg,transparent_0%,#000_6%,#000_94%,transparent_100%)]">
          <div className="brand-marquee-track flex w-max">
            {[0, 1].map((copy) =>
              marqueeBrands.map((brand, i) => (
                <div
                  key={`${copy}-${i}`}
                  aria-hidden={copy === 1}
                  className="flex-shrink-0 w-[150px] sm:w-[200px] lg:w-[260px] pr-[12px] lg:pr-[16px]"
                >
                  <BrandLogoCard logo={brand.logo} name={brand.name} />
                </div>
              ))
            )}
          </div>
        </div>

        {/* Testimonials */}
        <div className="mt-6 lg:mt-10 overflow-hidden">
          <div
            className="flex transition-transform duration-700 ease-in-out"
            style={{ transform: `translateX(-${current * 100}%)` }}
          >
            {slides.map((slide, i) => (
              <div key={i} className="w-full flex-shrink-0 grid grid-cols-1 lg:grid-cols-2 gap-[16px] lg:gap-6 p-[4px]">
                {slide.map((item) => (
                  <TestimonialCard key={item.name} {...item} />
                ))}
              </div>
            ))}
          </div>
        </div>

        <div className="mt-6 lg:mt-8 flex justify-center gap-[8px]">
          {slides.map((_, i) => (
            <button
              key={i}
              type="button"
              aria-label={`Show testimonials ${i + 1}`}
              onClick={() => setCurrent(i)}
              className={`w-[10px] h-[10px] rounded-full transition-colors ${
                current === i ? "bg-[#333]" : "bg-[#D9D9D9]"
              }`}
            />
          ))}
        </div>

        <a
          href={SIGNUP_URL}
          className="inline-block mt-10 lg:mt-20 rounded-full border-[1.5px] border-[#333] px-10 lg:px-[44px] py-[16px] lg:py-[22px] text-[#222] text-[16px] lg:text-[20px] hover:bg-[#333] hover:text-white transition-colors"
        >
          Join the Community
        </a>
      </div>
      <style>{`
        @keyframes brand-marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        .brand-marquee-track { animation: brand-marquee 45s linear infinite; }
        .brand-marquee:hover .brand-marquee-track { animation-play-state: paused; }
        @media (prefers-reduced-motion: reduce) { .brand-marquee-track { animation: none; } }
      `}</style>
    </section>
  );
};
